import { useCallback, useMemo } from 'react'

import { RoleType } from '@tribeplatform/gql-client/types'

import { useI18n } from '../../i18n/providers/I18nProvider.js'
import { Chart } from '../components/Chart.js'
import type { ChartProps } from '../types/index.js'
import { AnalyticsFeeder } from '../utils/analyticsFeeder.js'
import type { AnalyticsQuery } from '../utils/generateAnalyticsQuery.js'
import {
  generateAnalyticsQuery,
  generateTimeFrame,
  generateGroupBy,
} from '../utils/generateAnalyticsQuery.js'
import { DefaultChartColors } from '../utils/index.js'
import { getValueFromAnalyticsData } from '../utils/parser.js'

export const ActiveMembersArea = ({
  scope,
  id,
  timeFrame,
  className,
  segment,
}: ChartProps) => {
  const { $t } = useI18n()

  const createQueries = useCallback(() => {
    const generalQuery: AnalyticsQuery = {
      selects: [],
      timeFrame: generateTimeFrame({ timeFrame }),
      where: [
        { field: scope, value: id },
        { field: 'actor_type', operator: '!=', value: RoleType.guest },
        { field: 'actor_type', operator: '!=', value: null },
        { field: 'person', operator: '!=', value: null },
        ...(segment?.where ? [segment.where] : []),
      ],
      limit: timeFrame.xAxisCount + 2,
    }

    const currentQuery: AnalyticsQuery = {
      ...generalQuery,
      selects: [{ field: 'count(person)', as: 'Members' }],
    }

    const prevQuery: AnalyticsQuery = {
      ...currentQuery,
      timeFrame: generateTimeFrame({ timeFrame, target: 'previous' }),
    }

    const chartQuery: AnalyticsQuery = {
      ...currentQuery,
      groupBy: {
        ...generateGroupBy(timeFrame.granularity),
      },
      order: { by: 'timeBucket', dir: 'asc' },
    }

    if (timeFrame.id === 'allTime') {
      return [
        generateAnalyticsQuery(chartQuery),
        generateAnalyticsQuery(currentQuery),
      ]
    }

    return [
      generateAnalyticsQuery(chartQuery),
      generateAnalyticsQuery(currentQuery),
      generateAnalyticsQuery(prevQuery),
    ]
  }, [id, scope, segment, timeFrame])

  const queries = useMemo(() => [createQueries()], [createQueries])

  return (
    <AnalyticsFeeder queries={queries[0]}>
      {({ data, isLoading }) => (
        <Chart
          type="Area"
          title={$t(
            {
              id: 'Analytics.ActiveMembersArea.CharTitle',
              defaultMessage: 'Active {MEMBERS} ({granularity})',
              description: 'Title for active members chart',
            },
            { granularity: timeFrame.granularityText },
          )}
          description={timeFrame.text}
          toolTipText={$t({
            id: 'Analytics.ActiveMembersArea.ChartTooltipText',
            defaultMessage:
              '{MEMBERS_CC} that visited the {COMMUNITY} over time',
            description: 'Active members chart tooltip text',
          })}
          current={getValueFromAnalyticsData(data[1], 'Members')}
          previous={getValueFromAnalyticsData(data[2], 'Members')}
          data={data[0]}
          timeFrame={timeFrame}
          indexBy={['Members']}
          labels={[
            $t({
              id: 'Generics.Members',
              defaultMessage: '{MEMBERS_CC}',
            }),
          ]}
          syncId="analytics"
          isLoading={isLoading}
          className={className}
          ColorTheme={DefaultChartColors}
        />
      )}
    </AnalyticsFeeder>
  )
}
